// ============================================================
// TMR Portal Admin — o editor das faixas de score de uma regra (26 §9)
// ============================================================
import { Btn } from '../../components/primitives';
import { SelectField, TextField } from '../../components/fields';
import { Icons, StrokeIcon } from '../../lib/icons';
import { MAXIMUM_SCORE_BANDS, type ScoreBandDraft } from '../../data/businessRules/form';
import { ruleOptionCopy } from '../../data/businessRules/status';

/** Props for {@link ScoreBandsField}. */
interface ScoreBandsFieldProps {
  /** The rule: its key (for the actions' names), the range the bands cover and the actions they may carry. */
  rule: { key: string; minimum: number | null; maximum: number | null; options: string[] };
  bands: ScoreBandDraft[];
  onChange: (bands: ScoreBandDraft[]) => void;
  disabled: boolean;
}

/**
 * The bands of a score-bands rule, one row each: from, to and the action. Adding a band splits nothing on its
 * own — it appends a row starting right after the last one, and the operator moves the ends. Whether the
 * bands cover the whole range is checked on save, by `parseScoreBands` and again by the server, which is
 * where the message comes from (`26` §5).
 */
export function ScoreBandsField({ rule, bands, onChange, disabled }: ScoreBandsFieldProps) {
  const actions = rule.options.map((option) => ({ value: option, label: ruleOptionCopy(rule, option).label }));

  const update = (index: number, patch: Partial<ScoreBandDraft>) =>
    onChange(bands.map((band, i) => (i === index ? { ...band, ...patch } : band)));

  const remove = (index: number) => onChange(bands.filter((_, i) => i !== index));

  const add = () => {
    const last = bands[bands.length - 1];
    const next = last && /^\d+$/.test(last.ate.trim()) ? String(Number(last.ate) + 1) : String(rule.minimum ?? 0);
    onChange([...bands, { de: next, ate: String(rule.maximum ?? ''), acao: rule.options[0] ?? '' }]);
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {bands.map((band, index) => (
        <div
          // The rows have no identity of their own; editing never reorders them, so the index is stable.
          key={index}
          role="group"
          aria-label={`Faixa ${index + 1}`}
          style={{ display: 'grid', gridTemplateColumns: '84px 84px 1fr 34px', gap: 8, alignItems: 'end' }}
        >
          <TextField
            label={index === 0 ? 'De' : undefined}
            value={band.de}
            inputMode="numeric"
            onChange={(de) => update(index, { de })}
            disabled={disabled}
          />
          <TextField
            label={index === 0 ? 'Até' : undefined}
            value={band.ate}
            inputMode="numeric"
            onChange={(ate) => update(index, { ate })}
            disabled={disabled}
          />
          <SelectField
            label={index === 0 ? 'Ação' : undefined}
            value={band.acao}
            options={actions}
            onChange={(acao) => update(index, { acao })}
            disabled={disabled}
          />
          <button
            type="button"
            onClick={() => remove(index)}
            className="pa-icon-btn"
            style={{ width: 34, height: 34, marginBottom: 2 }}
            title="Remover faixa"
            aria-label={`Remover faixa ${index + 1}`}
            disabled={disabled || bands.length === 1}
          >
            <StrokeIcon d={Icons.x} size={15} />
          </button>
        </div>
      ))}

      <div>
        <Btn
          variant="secondary"
          size="sm"
          icon={Icons.plus}
          onClick={add}
          disabled={disabled || bands.length >= MAXIMUM_SCORE_BANDS}
        >
          Adicionar faixa
        </Btn>
      </div>
    </div>
  );
}
